import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getVehiclesFromDb } from '../api/getVehiclesFromDb';
import { vehicleCsvFields } from '../components/csv/vehicleCsvFields';
import { saveAs } from 'file-saver';
import { CircularProgress, Box, Typography } from '@mui/material';

const CsvDownloadPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<number | null>(null);
  const [totalPages, setTotalPages] = useState<number | null>(null);


  const parseQuery = () => {
    const params = new URLSearchParams(location.search);
    return {
      client: params.get('client'),
      start_date: params.get('start_date'),
      end_date: params.get('end_date'),
      fields: params.get('fields')?.split(',') || [],
    };
  };

  useEffect(() => {
    const fetchAndDownload = async () => {
      const { client, start_date, end_date, fields } = parseQuery();

      if (!client || !start_date || !end_date) {
        alert("Missing required query params.");
        setLoading(false);
        return navigate('/');
      }

      try {
        const perPage = 100;
        let page = 1;
        let totalVehicles = 0;
        let allVehicles: any[] = [];

        setCurrentPage(1); // first page being fetched

        // First call
        const firstResponse = await getVehiclesFromDb({
          page,
          per_page: perPage,
          system_code: client,
          start_date,
          end_date,
        });

        allVehicles = firstResponse.vehicles;
        totalVehicles = firstResponse.total;

        const total = Math.ceil(totalVehicles / perPage);
        setTotalPages(total);

        // Fetch remaining
        for (page = 2; page <= total; page++) {
          setCurrentPage(page);

          const response = await getVehiclesFromDb({
            page,
            per_page: perPage,
            system_code: client,
            start_date,
            end_date,
          });

          allVehicles.push(...response.vehicles);
        }

        const selectedFields = fields.length > 0
          ? vehicleCsvFields.filter(f => fields.includes(f.key))
          : vehicleCsvFields;

        const csvHeaders = selectedFields.map(f => f.label);
        const csvRows = allVehicles.map(v =>
          selectedFields.map(f => f.extractor ? f.extractor(v) : '')
        );

        const csvContent = [csvHeaders, ...csvRows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        saveAs(blob, `vehicles_export_${Date.now()}.csv`);
      } catch (err) {
        console.error("CSV download error:", err);
        alert("Error generating CSV.");
      } finally {
        setLoading(false);
        setCurrentPage(null);
        setTotalPages(null);
        navigate(-1);
      }
    };


    fetchAndDownload();
  }, []);

  if (!loading) return null;

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
    >
      <CircularProgress />
      <Typography variant="body1" mt={2}>
        Generating your CSV...
      </Typography>

      {currentPage && totalPages && (
        <Typography variant="body2" mt={1}>
          Downloading page {currentPage} of {totalPages}
        </Typography>
      )}
    </Box>
  );

};

export default CsvDownloadPage;
